import { createSlice, createAction } from '@reduxjs/toolkit'
import socketFactory, { mapSocketFactory } from '../../../utils/socketFactory';
import pokeSound from "../../../assets/bell-ring.mp3";
import { getStoredDevice } from '../../../utils/browserUtils';

const initialState = {
  // Media device states
  micActive: false,
  micLoading: false,
  selectedMic: getStoredDevice("selectedMic"),
  mics: [], // Array of device objects

  speakerActive: true,
  speakerLoading: false,
  selectedSpeaker: getStoredDevice("selectedSpeaker"),
  speakers: [], // Array of device objects

  cameraActive: false,
  cameraLoading: false,
  selectedCamera: getStoredDevice("selectedCamera"),
  cameras: [], // Array of device objects


  screenShareActive: false,
  screenShareLoading: false,

  // Camera effect state
  selectedEffect: "none",
  selectedBackground: localStorage.getItem("selectedBackground") || "room.jpg",




  // --- Device permission objects ---
  micPermission: null,
  cameraPermission: null,

  // Device monitoring states
  isMonitoringMicrophone: false,
  isMonitoringCamera: false,

  // Loading and error states
  loading: true,
  error: null,
};



const mediaControlsSlice = createSlice({
  name: 'mediaControls',
  initialState,
  reducers: {
    // Toggle actions for device states
    toggleMic: (state, action) => {
      state.micLoading = false;
      state.micActive = action.payload;
    },
    toggleSpeaker: (state, action) => {
      state.speakerLoading = false;
      state.speakerActive = action.payload;
    },
    toggleCamera: (state, action) => {
      state.cameraLoading = false;
      state.cameraActive = action.payload;
    },
    toggleScreenShare: (state, action) => {
      state.screenShareLoading = false;
      state.screenShareActive = action.payload;
    },
    setMicLoading: (state, action) => {
      state.micLoading = action.payload;
    },
    setSpeakerLoading: (state, action) => {
      state.speakerLoading = action.payload;
    },
    setCameraLoading: (state, action) => {
      state.cameraLoading = action.payload;
    },
    setScreenShareLoading: (state, action) => {
      state.screenShareLoading = action.payload;
    },
    // Setters for selected devices (now expects device objects)
    setSelectedMic: (state, action) => {
      try {
        localStorage.setItem('selectedMic', JSON.stringify(action.payload));
      }
      catch (e) { console.log(e) }
      state.selectedMic = action.payload
    },
    setSelectedSpeaker: (state, action) => {
      try {
        localStorage.setItem('selectedSpeaker', JSON.stringify(action.payload));
      }
      catch (e) { console.log(e) }
      state.selectedSpeaker = action.payload
    },
    setSelectedCamera: (state, action) => {
      try {
        localStorage.setItem('selectedCamera', JSON.stringify(action.payload));
      }
      catch (e) { console.log(e) }
      state.selectedCamera = action.payload
    },

    // Setters for active effects
    setSelectedEffect: (state, action) => {
      state.selectedEffect = action.payload
    },

    setSelectedBackground: (state, action) => {
      try {
        localStorage.setItem('selectedBackground', action.payload);
      }
      catch (e) { console.log(e) }

      state.selectedBackground = action.payload
    },

    // Setters for device lists (now expects arrays of device objects)
    setMics: (state, action) => {
      state.mics = action.payload
    },
    setSpeakers: (state, action) => {
      state.speakers = action.payload
    },
    setCameras: (state, action) => {
      state.cameras = action.payload
    },

    // Setters for device permissions
    setMicPermission: (state, action) => {
      state.micPermission = action.payload
    },
    setCameraPermission: (state, action) => {
      state.cameraPermission = action.payload
    },

    // Setters for device monitoring states
    setIsMonitoringMicrophone: (state, action) => {
      state.isMonitoringMicrophone = action.payload
    },
    setIsMonitoringCamera: (state, action) => {
      state.isMonitoringCamera = action.payload
    },
    // Loading state management
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    clearError: (state) => {
      state.error = null;
    },
    reset: () => initialState,
    // --- Poke event ---
    ON_POKE: (state) => {
      const audio = new Audio(pokeSound);
      console.log("Playing poke sound through selected speaker:", state.selectedSpeaker);
      if (state.selectedSpeaker && audio.setSinkId) {
        audio.setSinkId(state.selectedSpeaker.deviceId).catch((err) => {
          console.warn("Could not set sinkId for poke sound:", err);
          audio.play().catch((error) => {
            console.error("Error while playing poke audio", error);
          });
        });
      }
      audio.play().catch((error) => {
        console.error("Error while playing poke audio", error);
      });
    },

    ON_EXCLUDE_USER: (state) => {
            const audio = new Audio('room_leaving.wav');
            console.log("Playing poke sound through selected speaker:", state.selectedSpeaker);
            if (state.selectedSpeaker && audio.setSinkId) {
              audio.setSinkId(state.selectedSpeaker.deviceId).catch((err) => {
                console.warn("Could not set sinkId for leave room sound:", err);
                audio.play().catch((error) => {
                  console.error("Error while playing leave room audio", error);
                });
              });
            }
            audio.play().catch((error) => {
              console.error("Error while playing leave room audio", error);
            });
    },
    MIC_CLOSED: (state) => {
      console.log("MIC_CLOSED");
    },
    MIC_STATE_CONFIRMATION: (state) => {
      console.log("MIC_STATE_CONFIRMATION");
    },
    
  },
});

export const {
  toggleMic,
  toggleSpeaker,
  toggleCamera,
  toggleScreenShare,
  setMicLoading,
  setSpeakerLoading,
  setCameraLoading,
  setScreenShareLoading,
  setSelectedMic,
  setSelectedSpeaker,
  setSelectedCamera,
  setSelectedEffect,
  setSelectedBackground,
  setMics,
  setSpeakers,
  setCameras,
  setLoading,
  clearError,
  reset,
  setMicPermission,
  setCameraPermission,
  setIsMonitoringMicrophone,
  setIsMonitoringCamera,
  ON_POKE,
  MIC_CLOSED,
  MIC_STATE_CONFIRMATION ,
  ON_EXCLUDE_USER,

} = mediaControlsSlice.actions

export const startDeviceMonitoring = createAction('mediaControls/startDeviceMonitoring');
export const refreshDeviceList = createAction('mediaControls/refreshDeviceList');
export const startMonitoringCameraPermission = createAction('mediaControls/startMonitoringCameraPermission');
export const startMonitoringMicrophonePermission = createAction('mediaControls/startMonitoringMicrophonePermission');

mapSocketFactory.on('onexcludeUser', ON_EXCLUDE_USER);
mapSocketFactory.on('micClosed', MIC_CLOSED);
mapSocketFactory.on('micStateConfirmation', MIC_STATE_CONFIRMATION);
socketFactory.on('receivePoke', ON_POKE)

export const ACTIVE_AGENTS = socketFactory.emit("requestActiveAgentStatus", "socket/requestActiveAgentStatus");

export default mediaControlsSlice.reducer